import type { FlexProps } from '@chakra-ui/react';
import { Flex } from '@chakra-ui/react';
import { getPathInfo, useRealPathname } from '@ui/hooks/router';
import type { CoinMode } from '@ui/hooks/useCoinMode';
import { COIN_MODE, useCoinMode } from '@ui/hooks/useCoinMode';
import { useIsWhitelistFeatureEnabled } from '@ui/hooks/useIsFeatureEnabled';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { Card } from './Card';

export type StakeModeSwitchTabProps = Omit<FlexProps, 'children'> & {
  mode?: { coin: CoinMode; isStrategy: boolean };
  onModeChange: (coinMode: CoinMode, isStrategy: boolean) => void;
  children: (coinMode: CoinMode, isStrategy: boolean) => ReactNode;
};
export const StakeModeSwitchTab = ({
  mode: _mode,
  onModeChange: onCoinModeChange,
  children,
  ...props
}: StakeModeSwitchTabProps) => {
  const globalCoinMode = useCoinMode();
  const realPathName = useRealPathname();
  const pathInfo = getPathInfo(realPathName);
  const mode = _mode ?? {
    coin: globalCoinMode,
    isStrategy: pathInfo.category === 'strategy',
  };

  const isStrategyEnabled = useIsWhitelistFeatureEnabled('strategyVault');

  const items = useMemo(
    () =>
      isStrategyEnabled
        ? [
            { coin: COIN_MODE.cro.coinMode, isStrategy: false },
            { coin: COIN_MODE.atom.coinMode, isStrategy: false },
            { coin: COIN_MODE.cro.coinMode, isStrategy: true },
          ]
        : [
            { coin: COIN_MODE.cro.coinMode, isStrategy: false },
            { coin: COIN_MODE.atom.coinMode, isStrategy: false },
          ],
    [isStrategyEnabled],
  );

  return (
    <Flex direction="column" gap="4px" w="188px" flexShrink="0" {...props}>
      {items.map((item, i) => {
        const isActive =
          item.coin === mode.coin && item.isStrategy === mode.isStrategy;
        return (
          <Card
            bgImage={
              item.isStrategy
                ? "url('/images/pattern-liquidity.png')"
                : undefined
            }
            bgSize="cover"
            bgRepeat="no-repeat"
            bgPos="center center"
            whiteSpace="nowrap"
            key={i}
            variant="button"
            isActive={isActive}
            cursor="pointer"
            textStyle="h4Bold"
            color={isActive ? 'text-active' : 'text-inactive'}
            p="10px"
            textAlign="center"
            justifyContent="center"
            onClick={() => {
              onCoinModeChange(item.coin, item.isStrategy);
            }}
          >
            {children(item.coin, item.isStrategy)}
          </Card>
        );
      })}
    </Flex>
  );
};
